import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useDispatch, useSelector } from 'react-redux';

import { createCustomer, fetchCustomer, fetchCustomers } from '../api/customers';
import { setCustomer, selectCustomer } from '../store/cartSlice';

/**
 * Async customer search using MUI Autocomplete.
 * Dispatches setCustomer to Redux on selection.
 */
export default function CustomerSelect({ defaultCustomer }) {
  const dispatch = useDispatch();
  const currentCustomer = useSelector(selectCustomer);

  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [newCustomer, setNewCustomer] = useState({
    customerName: '',
    mobileNo: '',
    emailId: '',
  });

  // Set default customer from POS Profile on mount, including loyalty metadata.
  useEffect(() => {
    if (defaultCustomer && !currentCustomer) {
      let cancelled = false;
      fetchCustomer(defaultCustomer)
        .then((customer) => {
          if (!cancelled) {
            dispatch(setCustomer(customer || { name: defaultCustomer, customer_name: defaultCustomer }));
          }
        })
        .catch(() => {
          if (!cancelled) {
            dispatch(setCustomer({ name: defaultCustomer, customer_name: defaultCustomer }));
          }
        });
      return () => { cancelled = true; };
    }
    return undefined;
  }, [currentCustomer, defaultCustomer, dispatch]);

  // Debounced customer search
  useEffect(() => {
    if (!inputValue || inputValue.length < 2) {
      setOptions([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await fetchCustomers(inputValue);
        setOptions(results);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue]);

  const openCreateDialog = () => {
    setCreateError(null);
    setNewCustomer((prev) => ({
      ...prev,
      customerName: inputValue || prev.customerName,
    }));
    setCreateOpen(true);
  };

  const handleCreateCustomer = async () => {
    if (!newCustomer.customerName.trim()) {
      setCreateError('Customer name is required.');
      return;
    }

    setCreating(true);
    setCreateError(null);
    try {
      const created = await createCustomer({
        customerName: newCustomer.customerName.trim(),
        mobileNo: newCustomer.mobileNo.trim(),
        emailId: newCustomer.emailId.trim(),
      });
      dispatch(setCustomer(created));
      setOptions((prev) => [created, ...prev.filter((c) => c.name !== created.name)]);
      setInputValue(created.customer_name || created.name);
      setCreateOpen(false);
      setNewCustomer({ customerName: '', mobileNo: '', emailId: '' });
    } catch (err) {
      setCreateError(err?.response?.data?._server_messages || err.message || 'Failed to create customer');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box sx={{ p: 1.5, pb: 0 }}>
      <Stack direction="row" spacing={1}>
        <Autocomplete
          size="small"
          options={options}
          loading={loading}
          getOptionLabel={(opt) =>
            typeof opt === 'string' ? opt : opt.customer_name || opt.name || ''
          }
          isOptionEqualToValue={(opt, val) => opt.name === val.name}
          value={currentCustomer}
          onChange={(_, newValue) => {
            dispatch(setCustomer(newValue));
          }}
          inputValue={inputValue}
          onInputChange={(_, newInput) => setInputValue(newInput)}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search customer..."
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <PersonIcon color="action" sx={{ mr: 0.5, fontSize: 20 }} />
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
          noOptionsText={inputValue.length < 2 ? 'Type to search...' : 'No customers found'}
          clearOnBlur={false}
          sx={{ flexGrow: 1 }}
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={<PersonAddIcon />}
          onClick={openCreateDialog}
          sx={{ whiteSpace: 'nowrap' }}
        >
          New
        </Button>
      </Stack>

      <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create Customer</DialogTitle>
        <DialogContent>
          {createError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createError}
            </Alert>
          )}

          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Customer Name"
              value={newCustomer.customerName}
              onChange={(e) => setNewCustomer((prev) => ({ ...prev, customerName: e.target.value }))}
              autoFocus
              fullWidth
            />
            <TextField
              label="Mobile No"
              value={newCustomer.mobileNo}
              onChange={(e) => setNewCustomer((prev) => ({ ...prev, mobileNo: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={newCustomer.emailId}
              onChange={(e) => setNewCustomer((prev) => ({ ...prev, emailId: e.target.value }))}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateCustomer}
            disabled={creating || !newCustomer.customerName.trim()}
          >
            {creating ? <CircularProgress size={20} color="inherit" /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
